import { Action } from 'redux-actions';
import { put, takeLatest } from 'redux-saga/effects';

import { fetchCenters, fetchFranchisorSuccess, fetchTerms } from 'actions/auth';
import {
  appInitialize, appInitializeSuccess, changeLanguage,
  fetchLanguages,
  fetchLanguagesSuccess, fetchLookups, fetchLookupsSuccess, reloadLanguages, setSelectedLanguage,
} from 'actions/root';
import Config from 'config';
import { getExpirableData, getIsDataExpired } from 'helpers/common.helpers';
import httpHelpers from 'helpers/http.helpers';
import { store } from 'store';
import { IAppLookups, IFetchOptions, ILanguage } from 'types';

import analytics from '@app/helpers/analytics.helper';
import { setCurrencyOptions } from '@app/helpers/number.helpers';

function* appInitializeSaga() {
  try {
    yield httpHelpers.refreshToken();

    const stateData = store.getState();

    if (stateData.auth.franchisor && getIsDataExpired(stateData.auth.franchisor)) {
      const franchisor = yield httpHelpers.get(`${Config.SERVER_URL}/api/franchisors?domain=${encodeURIComponent(stateData.auth.franchisor.data.domain)}`);
      yield put(fetchFranchisorSuccess(getExpirableData({
        ...franchisor,
        domain: stateData.auth.franchisor.data.domain,
      }, 1, 'h')));
      setCurrencyOptions(franchisor.currency);
    } else if (stateData.auth.franchisor) {
      setCurrencyOptions(stateData.auth.franchisor.data.currency);
    }

    if (stateData.auth.franchisor) {
      yield put(fetchLanguages({ forceFetch: false }));
      yield put(fetchLookups({ forceFetch: false }));
    }

    if (stateData.auth.authenticated) {
      analytics.setUser({ userId: stateData.auth.authInfo.user.id });
      yield put(fetchTerms({ forceFetch: false }));
      yield put(fetchCenters({ forceFetch: false }));
    }

    yield put(appInitializeSuccess());
  } catch (err) {
    //
  }
}

export function* fetchLanguagesSaga(action: Action<IFetchOptions>) {
  try {
    const stateData = store.getState();
    if (!stateData.auth.franchisor) {
      return;
    }

    if (action.payload.forceFetch || getIsDataExpired(stateData.root.languages)) {
      const languages: ILanguage[] = yield httpHelpers.get<ILanguage[]>(`${Config.SERVER_URL}/api/languages`);
      yield put(fetchLanguagesSuccess(getExpirableData(languages, 1, 'h')));

      const selectedLanguage = store.getState().root.selectedLanguage;
      const defaultLang = languages.find(x => x.isDefault) || languages[0];
      if (!selectedLanguage || !languages.find(x => x.id === selectedLanguage.id)) {
        yield put(setSelectedLanguage(defaultLang));
      }
    }
  } catch (err) {
    //
  }
}

export function* fetchLookupsSaga(action: Action<IFetchOptions>) {
  try {
    const stateData = store.getState();
    if (!stateData.auth.franchisor) {
      return;
    }

    if (action.payload.forceFetch || getIsDataExpired(stateData.root.lookups)) {
      const lookups = yield httpHelpers.get<IAppLookups>(`${Config.SERVER_URL}/api/lookups`);
      yield put(fetchLookupsSuccess(getExpirableData(lookups, 1, 'h')));
    }
  } catch (err) {
    //
  }
}

function* reloadLanguagesSaga() {
  yield put(fetchLanguages({ forceFetch: true }));
  yield put(fetchLookups({ forceFetch: true }));
}

function* changeLanguageSaga() {
  yield put(fetchLookups({ forceFetch: true }));
}

export default [
  takeLatest(appInitialize.toString(), appInitializeSaga),
  takeLatest(fetchLanguages.toString(), fetchLanguagesSaga),
  takeLatest(fetchLookups.toString(), fetchLookupsSaga),
  takeLatest(reloadLanguages.toString(), reloadLanguagesSaga),
  takeLatest(changeLanguage.toString(), changeLanguageSaga),
];
